import { lazy, type ReactElement, Suspense } from "react";
import { Navigate, Route, Routes as RouterRoutes } from "react-router-dom";
import { ErrorBoundary } from "@/components/common/ErrorBoundary";
import { useAuth } from "@/contexts/AuthContext";

// Loading component for suspense fallback
const PageLoader = () => (
  <div className="flex min-h-screen items-center justify-center">
    <div className="flex flex-col items-center gap-4">
      <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
      <span className="text-muted-foreground">Loading...</span>
    </div>
  </div>
);

// Lazy load pages for code splitting
// Core pages (loaded immediately or with priority)
const HomePage = lazy(() => import("@/app/pages/HomePage"));
const ProvidersPage = lazy(() => import("@/app/pages/ProvidersPage"));
const PricingPage = lazy(() => import("@/app/pages/PricingPage"));

// Auth pages (Better Auth)
const SignInPage = lazy(() => import("@/app/pages/SignInPage"));
const SignUpPage = lazy(() => import("@/app/pages/SignupPage"));

// Quote pages
const CheckQuotePage = lazy(() => import("@/app/pages/CheckQuotePage"));
const CreateQuotePage = lazy(() => import("@/app/pages/CreateQuotePage"));
const GetQuotePage = lazy(() => import("@/app/pages/GetQuotePage"));
const GenerateQuotePage = lazy(() => import("@/app/pages/GenerateQuotePage"));

// Directory & Provider pages
const DirectoryPage = lazy(() => import("@/app/pages/DirectoryPage"));
const ProviderSignupPage = lazy(() => import("@/app/pages/ProviderSignupPage"));
const ProviderBenefitsPage = lazy(() => import("@/app/pages/ProviderBenefitsPage"));

// SEO Category pages
const PlumbersPage = lazy(() => import("@/app/pages/categories/PlumbersPage"));
const ElectriciansPage = lazy(() => import("@/app/pages/categories/ElectriciansPage"));
const RoofersPage = lazy(() => import("@/app/pages/categories/RoofersPage"));
const RenovationsPage = lazy(() => import("@/app/pages/categories/RenovationsPage"));

// Info pages
const AboutPage = lazy(() => import("@/app/pages/AboutPage"));
const ContactPage = lazy(() => import("@/app/pages/ContactPage"));
const FAQPage = lazy(() => import("@/app/pages/FAQPage"));
const TermsPage = lazy(() => import("@/app/pages/TermsPage"));
const PrivacyPage = lazy(() => import("@/app/pages/PrivacyPage"));
const RoadmapPage = lazy(() => import("@/app/pages/RoadmapPage"));

// Dashboard pages (only loaded when authenticated)
const DashboardHomePage = lazy(() => import("@/app/pages/dashboard/DashboardHomePage"));
const ProfilePage = lazy(() => import("@/app/pages/dashboard/ProfilePage"));
const MyQuotesPage = lazy(() => import("@/app/pages/dashboard/MyQuotesPage"));
const SubscriptionPage = lazy(() => import("@/app/pages/dashboard/SubscriptionPage"));
const InquiriesPage = lazy(() => import("@/app/pages/dashboard/InquiriesPage"));
const CustomersPage = lazy(() => import("@/app/pages/dashboard/CustomersPage"));
const AdminPage = lazy(() => import("@/app/pages/dashboard/AdminPage"));

// CRM (heavy, only for admin)
const CRMDashboard = lazy(() => import("@/pages/CRMDashboard"));

// Auth guard HOC
const ProtectedRoute = ({ children }: { children: ReactElement }) => {
  const { user, isLoading } = useAuth();

  if (isLoading) {
    return <PageLoader />;
  }

  if (!user) {
    return <Navigate to="/login" />;
  }
  return children;
};

// Provider guard HOC
const ProviderRoute = ({ children }: { children: ReactElement }) => {
  const { user, isLoading } = useAuth();

  if (isLoading) {
    return <PageLoader />;
  }

  if (!user) {
    return <Navigate to="/login" />;
  }

  if (user.userType !== "provider" && user.userType !== "admin") {
    return <Navigate to="/dashboard" />;
  }

  return children;
};

// Admin guard HOC
const AdminRoute = ({ children }: { children: ReactElement }) => {
  const { user, isLoading } = useAuth();

  if (isLoading) {
    return <PageLoader />;
  }

  if (!user) {
    return <Navigate to="/login" />;
  }

  if (user.userType !== "admin") {
    return <Navigate to="/dashboard" />;
  }
  return children;
};

// Wrap lazy component with Suspense and ErrorBoundary
const LazyPage = ({ children }: { children: ReactElement }) => (
  <ErrorBoundary>
    <Suspense fallback={<PageLoader />}>{children}</Suspense>
  </ErrorBoundary>
);

export function Routes() {
  return (
    <RouterRoutes>
      {/* Public routes */}
      <Route
        path="/"
        element={
          <LazyPage>
            <HomePage />
          </LazyPage>
        }
      />
      <Route
        path="/check-quote"
        element={
          <LazyPage>
            <CheckQuotePage />
          </LazyPage>
        }
      />
      <Route
        path="/create-quote"
        element={
          <LazyPage>
            <CreateQuotePage />
          </LazyPage>
        }
      />
      <Route
        path="/get-quote"
        element={
          <LazyPage>
            <GetQuotePage />
          </LazyPage>
        }
      />
      <Route
        path="/generate-quote"
        element={
          <LazyPage>
            <GenerateQuotePage />
          </LazyPage>
        }
      />
      <Route
        path="/directory"
        element={
          <LazyPage>
            <DirectoryPage />
          </LazyPage>
        }
      />
      <Route
        path="/providers"
        element={
          <LazyPage>
            <ProvidersPage />
          </LazyPage>
        }
      />
      <Route
        path="/pricing"
        element={
          <LazyPage>
            <PricingPage />
          </LazyPage>
        }
      />
      <Route path="/profile" element={<Navigate to="/dashboard/profile" />} />

      {/* Auth routes (Better Auth) */}
      <Route
        path="/login"
        element={
          <LazyPage>
            <SignInPage />
          </LazyPage>
        }
      />
      <Route
        path="/signup"
        element={
          <LazyPage>
            <SignUpPage />
          </LazyPage>
        }
      />
      <Route
        path="/sign-in"
        element={
          <LazyPage>
            <SignInPage />
          </LazyPage>
        }
      />
      <Route
        path="/sign-up"
        element={
          <LazyPage>
            <SignUpPage />
          </LazyPage>
        }
      />

      {/* Provider public pages */}
      <Route
        path="/provider-signup"
        element={
          <LazyPage>
            <ProviderSignupPage />
          </LazyPage>
        }
      />
      <Route
        path="/provider-benefits"
        element={
          <LazyPage>
            <ProviderBenefitsPage />
          </LazyPage>
        }
      />

      {/* SEO Category pages */}
      <Route
        path="/plumbers"
        element={
          <LazyPage>
            <PlumbersPage />
          </LazyPage>
        }
      />
      <Route
        path="/electricians"
        element={
          <LazyPage>
            <ElectriciansPage />
          </LazyPage>
        }
      />
      <Route
        path="/roofers"
        element={
          <LazyPage>
            <RoofersPage />
          </LazyPage>
        }
      />
      <Route
        path="/renovations"
        element={
          <LazyPage>
            <RenovationsPage />
          </LazyPage>
        }
      />

      {/* Info pages */}
      <Route
        path="/about"
        element={
          <LazyPage>
            <AboutPage />
          </LazyPage>
        }
      />
      <Route
        path="/contact"
        element={
          <LazyPage>
            <ContactPage />
          </LazyPage>
        }
      />
      <Route
        path="/faq"
        element={
          <LazyPage>
            <FAQPage />
          </LazyPage>
        }
      />
      <Route
        path="/help"
        element={
          <LazyPage>
            <FAQPage />
          </LazyPage>
        }
      />
      <Route
        path="/terms"
        element={
          <LazyPage>
            <TermsPage />
          </LazyPage>
        }
      />
      <Route
        path="/privacy"
        element={
          <LazyPage>
            <PrivacyPage />
          </LazyPage>
        }
      />
      <Route
        path="/roadmap"
        element={
          <LazyPage>
            <RoadmapPage />
          </LazyPage>
        }
      />

      {/* Protected dashboard routes */}
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute>
            <LazyPage>
              <DashboardHomePage />
            </LazyPage>
          </ProtectedRoute>
        }
      />
      <Route
        path="/dashboard/profile"
        element={
          <ProtectedRoute>
            <LazyPage>
              <ProfilePage />
            </LazyPage>
          </ProtectedRoute>
        }
      />
      <Route
        path="/dashboard/settings"
        element={
          <ProtectedRoute>
            <LazyPage>
              <ProfilePage />
            </LazyPage>
          </ProtectedRoute>
        }
      />
      <Route
        path="/dashboard/subscription"
        element={
          <ProviderRoute>
            <LazyPage>
              <SubscriptionPage />
            </LazyPage>
          </ProviderRoute>
        }
      />
      <Route
        path="/dashboard/quotes"
        element={
          <ProtectedRoute>
            <LazyPage>
              <MyQuotesPage />
            </LazyPage>
          </ProtectedRoute>
        }
      />
      <Route
        path="/dashboard/inquiries"
        element={
          <ProviderRoute>
            <LazyPage>
              <InquiriesPage />
            </LazyPage>
          </ProviderRoute>
        }
      />
      <Route
        path="/dashboard/customers"
        element={
          <ProviderRoute>
            <LazyPage>
              <CustomersPage />
            </LazyPage>
          </ProviderRoute>
        }
      />
      <Route
        path="/dashboard/admin"
        element={
          <AdminRoute>
            <LazyPage>
              <AdminPage />
            </LazyPage>
          </AdminRoute>
        }
      />
      <Route
        path="/crm"
        element={
          <AdminRoute>
            <LazyPage>
              <CRMDashboard />
            </LazyPage>
          </AdminRoute>
        }
      />

      {/* Catch-all redirect */}
      <Route path="*" element={<Navigate to="/" />} />
    </RouterRoutes>
  );
}
